import i18n from 'src/utils/translate';
import { Fragment, useEffect, useState } from 'react';
import { Modal } from 'reactstrap';
import Button from 'src/components/buttons/button';
import { Post, Reply } from 'src/types';
import { getDisplayImagePreference, setDisplayImagePreference, COMMUNITIES_DEFAULT_HIDE_IMAGE } from 'src/utils/imagePreferences';
import { useHelpScout, useIsPoll } from 'src/utils/hooks';
import LazyImage from 'src/utils/lazyImage';
import styled from 'styled-components';
import { LinkButton } from 'styles-js/buttons';
import { textSmall } from 'styles-js/mixins/typography';
import Icon from 'src/components/icon';

const PlaceMarker = styled.div`
  padding: 15px;
  background: ${({ theme }) => theme.colorBlueBackground};
  margin-bottom: 30px;

  @media (${({ theme }) => theme.overScreenXsmall}) {
    padding: 30px 15px;
    text-align: center;
  }
`;

const Text = styled.p`
  margin-bottom: 10px;
`;

const ActionsContainer = styled.div`
  @media (${({ theme }) => theme.overScreenXsmall}) {
    display: inline-flex;
  }
`;

const StyledButton = styled(Button)`
  margin-right: 16px;

  @media (${({ theme }) => theme.underScreenXsmall}) {
    margin-bottom: 10px;
  }
`;

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  margin-bottom: 0;
  font-size: ${({ theme }) => theme.fontSizeXs};
  cursor: pointer;
`;

const Input = styled.input`
  width: 15px;
  height: 15px;
  margin-right: 8px;
`;

const PreviewImage = styled.div`
  display: inline-block;
  max-width: 350px;
  margin-bottom: 30px;
  border: ${({ theme }) => theme.greyLine};
`;

const FullscreenButton = styled.button`
  position: relative;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  overflow: hidden;

  &:focus, &:hover, &:active {
    span {
      border-color: ${({ theme }) => theme.colorBlue};
    }
  }

  img {
    max-width: 100%;
  }
`;

const FullscreenIcon = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 5px 8px 7px;
  border-radius: 4px;
  border: ${({ theme }) => theme.greyLine};
  background-color: ${({ theme }) => theme.colorWhite};
  box-shadow: 0 0 6px ${({ theme }) => theme.colorBlackHover};
  font-size: 22px;
  transition: all 0.7s ease;
`;

const Caption = styled.span`
  display: block;
  padding: 10px;
  ${textSmall};
  color: ${({ theme }) => theme.colorGreyDark};
`;

const ImageModal = styled(Modal)`
  .modal &.modal-dialog {
    max-width: none;
    margin: 0;
  }

  .modal-content {
    background-color: transparent;
    background-clip: border-box;
    border: none;

    height: calc(100vh - 75px);
    margin: 60px auto 15px;
    align-items: center;
    justify-content: center;
  }

  img {
    max-width: 100vw;
    max-height: calc(100vh - 75px);
  }
`;

const CloseButton = styled(Button)`
  position: absolute;
  top: 15px;
  right: 15px;
  margin: 0;
  z-index: ${({ theme }) => theme.zindexLiftTop};
`;

type PostImage = {
  post: Post | Reply,
  communityId: number,
  fullscreen?: boolean,
};

export default function PostImage({ post, communityId, fullscreen = false }: PostImage) {
  const isHiddenImageCommunity = COMMUNITIES_DEFAULT_HIDE_IMAGE.includes(communityId);
  const isPoll = useIsPoll();

  const [shouldDisplayImage, setShouldDisplayImage] = useState<boolean>(!isHiddenImageCommunity);
  const [rememberMyChoice, setRememberMyChoice] = useState<boolean>(false);
  const [shouldDisplayImageModal, setShouldDisplayImageModal] = useState<boolean>(false);

  useEffect(() => {
    setShouldDisplayImage(getDisplayImagePreference(communityId));
  }, [communityId]);

  if (!post.imageUrl || isPoll) return null;

  const onShowImage = () => {
    if (rememberMyChoice) setDisplayImagePreference(communityId, true);
    setShouldDisplayImage(true);
  };

  const showPreview = post.imagePreview && fullscreen;
  const imageDescription = post?.imageDescription || '';

  return (
    <Fragment>
      {!shouldDisplayImage &&
        <PlaceMarker data-test-id="post-image-placemarker">
          <Text>
            {i18n.t('Images are hidden by default on this community.')}{' '}
            <LinkButton onClick={() => useHelpScout('142-images-hidden-by-default')}>{i18n.t('Learn more')}</LinkButton>
          </Text>
          <ActionsContainer>
            <StyledButton data-test-id="showImageBtn" text={i18n.t('Show image')}
              type="primary" onClick={onShowImage} />
            <Label>
              <Input checked={rememberMyChoice} type="checkbox"
                onChange={() => setRememberMyChoice(!rememberMyChoice)} />
              {i18n.t('Remember my choice on this device')}
            </Label>
          </ActionsContainer>
        </PlaceMarker>
      }

      {shouldDisplayImage && (
        showPreview ? (
          <PreviewImage>
            <FullscreenButton
              aria-label="View the image in full screen mode"
              className="btn"
              data-test-id="fullscreen-button"
              onClick={() => setShouldDisplayImageModal(true)}
            >
              <LazyImage alt={imageDescription} src={post.imagePreview} />
              <FullscreenIcon className="icon icon-zoom-in" />
            </FullscreenButton>
            {imageDescription && <Caption>{imageDescription}</Caption>}
          </PreviewImage>
        ) : (
          <LazyImage alt={imageDescription} classnames="post-image"
            src={post.imageUrl} />
        )
      )}

      {shouldDisplayImageModal &&
        <ImageModal
          backdropClassName="app-modal__backdrop"
          external={
            <CloseButton type="blank" onClick={() => setShouldDisplayImageModal(!shouldDisplayImageModal)}>
              {i18n.t('Close')}{' '}<Icon icon="close" />
            </CloseButton>
          }
          isOpen={shouldDisplayImageModal}
        >
          <img alt={imageDescription} src={post.imageUrl} />
        </ImageModal>
      }
    </Fragment>
  );
}
